import "./comptinformations.css";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import API from "../../../API";
import Swal from "sweetalert2";
import Joi from "joi";
import { useTranslation } from 'react-i18next';
import {
  FaEnvelope, FaPhone, FaLock, FaShieldAlt, FaEye, FaEyeSlash, FaCheckCircle
} from "react-icons/fa";

export default function Comptinformations({ userData }) {
  const { t } = useTranslation(); // Initialize the translation hook

  const [formData, setFormData] = useState({
    email: userData?.email || "",
    phone: userData?.phone || "",
    password: "",
  });
  const [showPsw, setShowPsw] = useState(false);

  // Joi schema for validation
  const schema = Joi.object({
    password: Joi.string().min(8).required().label(t("comptinformations.password_label")),
    phone: Joi.string()
      .pattern(/^[0-9]{10}$/) // Ensures phone has exactly 10 digits
      .required()
      .label(t("comptinformations.phone_label")),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form data using Joi schema
    const { error } = schema.validate({
      password: formData.password,
      phone: formData.phone,
    });

    if (error) {
      // Show SweetAlert2 error for validation failure
      Swal.fire({
        icon: 'error',
        title: t("comptinformations.validation_error"),
        text: error.details[0].message,
        confirmButtonText: 'OK',
      });
      return; // Stop form submission if validation fails
    }

    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("protoken");

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const updatedData = {
        phone: formData.phone,
        hash: formData.password || undefined,
      };

      await API.patch(`/users/${userId}`, updatedData, config);

      // Success alert
      Swal.fire({
        icon: 'success',
        title: t("comptinformations.success_message"),
        text: t("comptinformations.success_text"),
        confirmButtonText: 'OK',
      }).then(() => {
        window.location.href = '/profile';
      });
    } catch (error) {
      console.error("Error updating account information:", error);

      // Error alert
      Swal.fire({
        icon: 'error',
        title: t("comptinformations.error_message"),
        text: t("comptinformations.error_text"),
        confirmButtonText: 'OK',
      });
    }
  };

  return (
    <form className="ci-form" onSubmit={handleSubmit}>
      <header className="ci-head">
        <span className="ci-eyebrow">
          <FaShieldAlt /> {t("comptinformations.title")}
        </span>
        <p className="ci-sub">
          Update your contact details and password to keep your account secure.
        </p>
      </header>

      <div className="ci-section">
        <h3 className="ci-section-title">Contact</h3>
        <div className="ci-grid">
          <div className="ci-field">
            <label className="ci-label">
              <FaEnvelope className="ci-label-ico" />
              {t("comptinformations.email_label")}
              <span className="ci-badge">Read only</span>
            </label>
            <input
              type="email"
              className="ci-input is-readonly"
              placeholder={t("comptinformations.email_placeholder")}
              name="email"
              value={formData.email}
              onChange={handleChange}
              readOnly
            />
          </div>

          <div className="ci-field">
            <label className="ci-label">
              <FaPhone className="ci-label-ico" />
              {t("comptinformations.phone_label")}
            </label>
            <input
              type="tel"
              className="ci-input"
              placeholder={t("comptinformations.phone_placeholder")}
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>

      <div className="ci-section">
        <h3 className="ci-section-title">Security</h3>
        <div className="ci-grid">
          <div className="ci-field ci-field--full">
            <label className="ci-label">
              <FaLock className="ci-label-ico" />
              {t("comptinformations.password_label")}
            </label>
            <div className="ci-pwd">
              <input
                type={showPsw ? "text" : "password"}
                className="ci-input"
                placeholder={t("comptinformations.password_placeholder")}
                name="password"
                value={formData.password}
                onChange={handleChange}
              />
              <button
                type="button"
                className="ci-pwd-toggle"
                onClick={() => setShowPsw((s) => !s)}
                aria-label={showPsw ? "Hide password" : "Show password"}
              >
                {showPsw ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <Link
              to={`/forgot-password${
                userData?.email ? `?email=${encodeURIComponent(userData.email)}` : ""
              }`}
              className="ci-forgot"
            >
              {t("comptinformations.forgot_password")}
            </Link>
          </div>
        </div>
      </div>

      <div className="ci-actions">
        <button type="submit" className="ci-submit">
          <FaCheckCircle /> {t("comptinformations.update_button")}
        </button>
      </div>
    </form>
  );
}